import { css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { FormState } from 'react-hook-form';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { Input } from '../../../../../web/components-v2/Input';
import { IAddress, IDonorInfoCollectionOptions } from '../../../../models/Api';
import { IFormValues } from '../utils';
import { AddressInput } from './AddressInput';
import { ErrorMessage } from './ErrorMessage';
import { styleSheet } from './style';

type Props = {
	firstName: string;
	lastName: string;
	donorName: string;
	email: string;
	phone: string;
	address: IAddress;
	collectDonorPhone: IDonorInfoCollectionOptions;
	collectDonorAddress: IDonorInfoCollectionOptions;
	allowAnonymousDonations?: boolean;
	isAnonymous: boolean;
	setValue: (name: keyof IFormValues, value: string | boolean | IAddress) => void;
	formState?: FormState<IFormValues>;
};

export const CustomerDataCollection = ({
	firstName,
	lastName,
	donorName,
	email,
	phone,
	address,
	collectDonorPhone,
	collectDonorAddress,
	allowAnonymousDonations,
	isAnonymous,
	setValue,
	formState,
}: Props) => {
	const [isDonorNameFocused, setIsDonorNameFocused] = useState(false);
	const derivedDonorName = [firstName, lastName].filter(Boolean).join(' ');

	// An anonymous gift stores nothing but the receipt email, so asking for a phone or street address
	// would collect details that are discarded on save.
	const showPhone = useMemo(() => {
		return !isAnonymous && (collectDonorPhone.collect || collectDonorPhone.require);
	}, [collectDonorPhone, isAnonymous]);

	const showAddress = useMemo(() => {
		return !isAnonymous && (collectDonorAddress.collect || collectDonorAddress.require);
	}, [collectDonorAddress, isAnonymous]);

	return (
		<div className={css(styleSheet.container)}>
			{allowAnonymousDonations && (
				<div className={css(styleSheet.anonymousToggleContainer)}>
					<Checkbox
						id='donation-survey-anonymous'
						checked={isAnonymous}
						onCheckedChange={checked => {
							const anonymous = checked === true;
							setValue('isAnonymous', anonymous);
							if (anonymous) {
								setValue('firstName', '');
								setValue('lastName', '');
								setValue('donorName', '');
							}
						}}
					/>
					<label htmlFor='donation-survey-anonymous'>Make my donation anonymous</label>
				</div>
			)}

			<div className={css(styleSheet.customerInfoContainer)}>
				{!isAnonymous && (
					<>
						<div className={css(styleSheet.inputContainer)}>
							<label htmlFor='donation-survey-customer-first-name'>
								First Name <span className={css(styleSheet.required)}>*</span>
							</label>

							<Input
								id='donation-survey-customer-first-name'
								value={firstName}
								onChange={e => setValue('firstName', e.target.value)}
							/>
							{formState?.errors?.firstName && <ErrorMessage message={formState.errors.firstName.message} />}
						</div>

						<div className={css(styleSheet.inputContainer)}>
							<label htmlFor='donation-survey-customer-last-name'>
								Last Name <span className={css(styleSheet.required)}>*</span>
							</label>

							<Input
								id='donation-survey-customer-last-name'
								value={lastName}
								onChange={e => setValue('lastName', e.target.value)}
							/>
							{formState?.errors?.lastName && <ErrorMessage message={formState.errors.lastName.message} />}
						</div>

						<div className={css(styleSheet.inputContainer)}>
							<label htmlFor='donation-survey-customer-donor-name'>Donor Name / Company</label>

							<Input
								id='donation-survey-customer-donor-name'
								value={isDonorNameFocused ? donorName : donorName || derivedDonorName}
								onChange={e => setValue('donorName', e.target.value)}
								onFocus={() => {
									setIsDonorNameFocused(true);
									// materialize the derived default so it can be edited in place instead of vanishing
									if (!donorName && derivedDonorName) {
										setValue('donorName', derivedDonorName);
									}
								}}
								onBlur={() => {
									setIsDonorNameFocused(false);
									// blank or unchanged-from-default resumes live-tracking the name fields
									if (!donorName.trim() || donorName.trim() === derivedDonorName) {
										setValue('donorName', '');
									}
								}}
							/>
						</div>
					</>
				)}

				<div className={css(styleSheet.inputContainer)}>
					<label htmlFor='donation-survey-customer-email'>
						Email <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input id='donation-survey-customer-email' value={email} onChange={e => setValue('email', e.target.value)} />
					{isAnonymous && (
						<p className={css(styleSheet.fieldHint)}>
							We&apos;ll only use this to send your tax receipt. Your name won&apos;t appear anywhere in our records.
						</p>
					)}
					{formState?.errors?.email && <ErrorMessage message={formState.errors.email.message} />}
				</div>

				{showPhone && (
					<div className={css(styleSheet.inputContainer)}>
						<label htmlFor='donation-survey-customer-phone'>
							Phone {collectDonorPhone.require && <span className={css(styleSheet.required)}>*</span>}
						</label>
						<Input
							id='donation-survey-customer-phone'
							value={phone}
							onChange={e => setValue('phone', e.target.value)}
						/>
						{formState?.errors?.phone && <ErrorMessage message={formState.errors.phone.message} />}
					</div>
				)}
			</div>
			{showAddress && (
				<div className={css(styleSheet.addressContainer)}>
					<AddressInput
						address={address}
						collectDonorAddress={collectDonorAddress}
						onAddressChange={updatedAddress => setValue('address', updatedAddress)}
						formState={formState}
					/>
				</div>
			)}
		</div>
	);
};
